import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList,
  faChevronUp,
  faChevronDown,
  faTimes,
  faCheckCircle,
  faArrowRight,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import { useOrganization } from '@clerk/clerk-react';
import { OrganizationType } from 'modules/Common/utils/Utility';
import { useTranslation } from 'react-i18next';
import { useFetchGuidingChecklists } from '../hooks/useGuidingChecklists';
import GuidingChecklistPanel from './GuidingChecklistPanel';

/**
 * The floating "Onboarding" widget — the same guided path Home shows, in a
 * docked panel.
 *
 * It renders `GuidingChecklistPanel` rather than a second layout of its own, so
 * the rail, the open step's how-to and the completion state are drawn once and
 * read the same on both surfaces. Only the frame differs: Home gets a grid cell
 * that flexes to ~620px, this gets a fixed 560px column capped to the viewport.
 * `OnboardingGuidedPath` sizes itself to whatever height it is given (`h-full`
 * + an internally scrolling rail), so the panel needs no floating-specific
 * variant.
 *
 * The panel is mounted only while it can be SEEN, which outlasts `isOpen` by
 * the length of the slide (`SLIDE_MS`) so closing animates back into the edge
 * instead of vanishing. Both surfaces read the same checklist query, so a step
 * ticked here is ticked on Home too.
 *
 * ## Parked at the edge
 *
 * At rest the pill sits mostly off the right of the viewport, showing only its
 * icon; hover, focus or a click slides it back in. It is a persistent, app-wide
 * ornament on every page a declarant visits, and at full width it was competing
 * with each page's own bottom-right controls for the same corner. Parking it
 * keeps the affordance permanently reachable while giving the corner back.
 *
 * Everything here moves by `transform` only — never `right`/`width` — so the
 * slide runs on the compositor and no page ever reflows behind it.
 */

// One duration for the pill and the panel, so they read as one object moving.
// Also how long the panel stays mounted after `isOpen` goes false.
const SLIDE_MS = 300;

// How much of the parked pill stays on screen. Measured against the real
// stylesheet, not the token: the pill wore `px-4`, but BOOTSTRAP's `.px-4` is
// `!important` and won, so the padding was 24px rather than Tailwind's 16 —
// the trap CLAUDE.md documents — which pinned the icon 24px in and left it 7px
// off the clip. `px-[14px]` has no Bootstrap counterpart to lose to.
//
// 32px of peek is about the floor: the icon ends 27px in (14 of padding + 13 of
// glyph), so this leaves it 5px of air before the viewport crops it. Hiding the
// pill any further means tightening the padding first, or the icon itself
// starts getting cut.
//
// The pill's accessible name is unaffected — the label is clipped, not removed
// — so it is still "Onboarding 33%" to a screen reader and to the E2E spec that
// finds it by name.
const PARKED = 'translate-x-[calc(100%-2rem)]';

const GuidingChecklistFloatingWidget = () => {
  const { t } = useTranslation('onboarding');
  const { organization } = useOrganization();
  const isDeclarant = organization?.publicMetadata?.type === OrganizationType.Declarant;

  const { data: checklists } = useFetchGuidingChecklists({ enabled: isDeclarant });

  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  // Compute active checklist for the bar display
  const activeChecklist = useMemo(() => {
    if (!checklists) return null;
    return checklists.find((c) => !c.dismissed) || null;
  }, [checklists]);

  const percentage = useMemo(() => {
    if (!activeChecklist) return 0;
    const items = activeChecklist.items || [];
    const done = items.filter((i) => i.completed).length;
    return items.length > 0 ? Math.round((done / items.length) * 100) : 0;
  }, [activeChecklist]);

  // All complete or dismissed → hide widget
  const isComplete = activeChecklist ? activeChecklist.isComplete : true;

  // --- Completion notification ---
  const [notification, setNotification] = useState(null);
  const prevItemsRef = useRef(null);
  const isInitialLoadDone = useRef(false);

  useEffect(() => {
    const items = activeChecklist?.items || [];
    const prev = prevItemsRef.current;
    prevItemsRef.current = items;

    // Wait for initial data to load before tracking changes
    if (!isInitialLoadDone.current) {
      if (items.length > 0) isInitialLoadDone.current = true;
      return;
    }

    // Find the newly completed item
    const newlyDone = items.find(
      (item) => item.completed && !prev.find((p) => p.key === item.key)?.completed,
    );
    if (!newlyDone) return;

    const nextItem = items.find((item) => !item.completed);
    const allDone = items.every((item) => item.completed);

    setNotification({ completedName: newlyDone.name, nextName: nextItem?.name, allDone });
  }, [activeChecklist?.items]);

  // Auto-dismiss notification
  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotification(null), 5000);
    return () => clearTimeout(timer);
  }, [notification]);

  // --- Slide ---
  //
  // Two flags rather than one. `panelMounted` keeps the panel in the tree for
  // the length of the close so it has something to animate with; `panelIn` is
  // what the transition actually runs on, and it flips one frame AFTER mount,
  // because an element that appears already in its final position has no
  // starting point to move from and the browser skips the animation.
  const [panelMounted, setPanelMounted] = useState(false);
  const [panelIn, setPanelIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPanelMounted(true);
      return undefined;
    }
    setPanelIn(false);
    const timer = setTimeout(() => setPanelMounted(false), SLIDE_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!panelMounted || !isOpen) return undefined;
    const raf = requestAnimationFrame(() => setPanelIn(true));
    return () => cancelAnimationFrame(raf);
  }, [panelMounted, isOpen]);

  // A completion toast un-parks the pill so the two line up on the same edge —
  // a 300px card floating beside a pill hiding off-screen reads as two
  // unrelated things.
  const parked = !isOpen && !notification;

  // Click outside closes panel
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        // Don't close if clicking inside a modal (dialogs render in a portal)
        if (e.target.closest('.modal, [role="dialog"]')) return;
        // Nor for the walkthrough overlay, which is portalled to the body and
        // covers the page: its scrim closes the video, and dropping the panel
        // behind it would leave the reader back at the pill.
        if (e.target.closest('[data-onboarding-video-lightbox]')) return;
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  // Escape key closes panel
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Don't render for non-declarants or when no active checklist
  if (!isDeclarant || !activeChecklist || isComplete) return null;

  return (
    // The pill floats above every page at z-[1048], so a page whose own
    // sticky action bar owns the bottom edge would have its primary button
    // covered — the upload hub's wizard footer was fully hidden behind this.
    // Such pages raise `--cg-fab-bottom` for as long as that bar is on screen;
    // everywhere else the variable is unset and this stays at its 1rem inset.
    //
    // Anchored to `right-0`, not `right-4`, because the pill's parked position
    // is flush with the viewport edge and its travel is expressed entirely as a
    // transform. The cards above it keep the old 1rem inset with `mr-4` — a
    // margin, not a translate, because `animate-in` drives `transform` and a
    // static translate on the same element is overwritten for the length of the
    // keyframes and then snaps back.
    <div
      ref={panelRef}
      className="fixed right-0 z-[1048] flex flex-col items-end"
      style={{ bottom: 'var(--cg-fab-bottom, 1rem)' }}
    >
      {/* Panel — Home's card chrome (white, rounded-xl, slate border, p-4
          around a min-h-0 content box), on a fixed column instead of a grid
          cell. 560px is the room the rail wants before it starts spreading
          one-line rows apart; the viewport cap keeps it clear of the pill
          below it on a short screen, and the rail scrolls inside whatever is
          left. */}
      {panelMounted && (
        <div
          className={`mb-2 mr-4 w-[420px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-6.5rem)] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none ${
            panelIn ? 'translate-x-0 opacity-100' : 'pointer-events-none translate-x-full opacity-0'
          }`}
        >
          {/* Just enough header to close it. The path draws its own headline,
              goal tabs and dismiss control, so this row stays out of their way
              rather than repeating them. */}
          <div className="shrink-0 flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100">
            <span className="flex items-center gap-2 min-w-0">
              <FontAwesomeIcon
                icon={faClipboardList}
                className="text-primaryColor text-[13px] shrink-0"
              />
              <span className="text-[13px] font-bold text-slate-900 truncate">
                {t('checklist.gettingStarted', 'Getting started')}
              </span>
            </span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              title={t('common:actions.close', 'Close')}
              aria-label={t('common:actions.close', 'Close')}
              className="w-[26px] h-[26px] shrink-0 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
            >
              <FontAwesomeIcon
                icon={faTimes}
                className="text-[13px]"
              />
            </button>
          </div>

          {/* The same panel Home renders, given a definite height to fill. */}
          <div className="flex-1 min-h-0 overflow-hidden p-4">
            <GuidingChecklistPanel />
          </div>
        </div>
      )}

      {/* Completion notification */}
      {notification && !isOpen && (
        <div className="relative mb-2 mr-4 w-[300px] bg-white rounded-xl shadow-2xl border border-slate-200 p-4 animate-in slide-in-from-bottom-2 fade-in duration-300">
          <button
            type="button"
            onClick={() => setNotification(null)}
            title={t('common:actions.dismiss', 'Dismiss')}
            aria-label={t('common:actions.dismiss', 'Dismiss')}
            className="absolute top-2 right-2 w-[22px] h-[22px] inline-flex items-center justify-center rounded-lg text-slate-300 hover:text-slate-500 hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
          >
            <FontAwesomeIcon
              icon={faTimes}
              className="text-[11px]"
            />
          </button>
          <div className="flex items-start gap-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${notification.allDone ? 'bg-amber-100' : 'bg-primaryColor/10'}`}
            >
              <FontAwesomeIcon
                icon={notification.allDone ? faTrophy : faCheckCircle}
                className={notification.allDone ? 'text-amber-500' : 'text-primaryColor'}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-slate-900 m-0 leading-snug">
                {notification.allDone
                  ? t('checklist.allDone', 'All done!')
                  : t('checklist.itemCompleted', '"{{name}}" completed!', {
                      name: notification.completedName,
                    })}
              </p>
              <p className="text-[11.5px] text-slate-500 leading-snug mt-1 mb-0">
                {notification.allDone ? (
                  t('checklist.allDoneBody', "You've finished all onboarding steps. Great job!")
                ) : (
                  <>
                    {t('checklist.upNext', 'Up next:')}{' '}
                    <span className="font-medium text-slate-700">{notification.nextName}</span>
                  </>
                )}
              </p>
              {!notification.allDone && (
                <button
                  onClick={() => {
                    setNotification(null);
                    setIsOpen(true);
                  }}
                  className="inline-flex items-center gap-1 text-[11.5px] font-medium text-primaryColor hover:underline mt-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 rounded"
                >
                  {t('common:actions.continue', 'Continue')}{' '}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="text-[10px]"
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pill + rescue strip.
          `whitespace-nowrap` so the clipped label never wraps to a second line
          while the pill is parked, and `shrink-0` so the panel above it cannot
          squeeze it in the column. Hover and keyboard focus both bring it fully
          out, which is what makes an icon-sized tab discoverable.

          ## Why the wrapper and the invisible sibling exist

          Hover starts OFF the pill on a downstroke: as the pill slides out its
          right edge passes the cursor and hover is lost — parks it — puts it
          back under the cursor. The earlier fix widened a `group-hover` zone
          to the pill's whole layout box, which cured the flicker but also
          covered the empty space to the LEFT of the parked peek, so any cursor
          in the corner un-parked the pill unwarranted.

          `peer` fixes both sides. The rescue span is a sibling of the pill,
          anchored to the wrapper's right edge — where the pill's OUT position
          leaves a 1rem gap the cursor can land in. Nothing else triggers
          un-parking: the pill's own `hover:` covers the visible peek and the
          off-screen tail, `peer-hover:` covers the gap, and clear space to the
          pill's left belongs to the page again. */}
      <div className="relative shrink-0">
        <span
          aria-hidden="true"
          className="peer pointer-events-auto absolute right-0 top-0 h-full w-4"
        />
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          className={`flex items-center gap-2 whitespace-nowrap px-[14px] py-2.5 bg-primaryColor text-white rounded-full shadow-lg hover:bg-primaryColor-dark transition-[transform,background-color] duration-300 ease-out motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 ${
            parked
              ? `${PARKED} peer-hover:-translate-x-4 hover:-translate-x-4 focus-visible:-translate-x-4`
              : '-translate-x-4'
          }`}
        >
          <FontAwesomeIcon
            icon={faClipboardList}
            className="text-[13px]"
          />
          <span className="text-[13px] font-semibold">
            {t('checklist.onboarding', 'Onboarding')}
          </span>
          <span className="w-px h-4 bg-white/30" />
          <span className="text-[13px] font-bold">{percentage}%</span>
          <FontAwesomeIcon
            icon={isOpen ? faChevronDown : faChevronUp}
            className="text-[11px] ml-1"
          />
        </button>
      </div>
    </div>
  );
};

export default GuidingChecklistFloatingWidget;
